import React, { useState, useEffect } from 'react';
import { Input, Button, List, Typography, Alert, Space, Divider } from 'antd';
import { PlayCircleOutlined, CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons';

const { TextArea } = Input;
const { Text } = Typography;

const RegexTester = ({ value, onChange, placeholder }) => {
  const [testStrings, setTestStrings] = useState('');
  const [testResults, setTestResults] = useState([]);
  const [regexError, setRegexError] = useState('');

  // Test the regex against the test strings
  const testRegex = React.useCallback(() => {
    if (!value || !testStrings.trim()) {
      setTestResults([]);
      return;
    }

    try {
      const regex = new RegExp(value);
      const lines = testStrings.split('\n').filter(line => line.trim());
      
      const results = lines.map(line => {
        const match = regex.test(line);
        const execResult = regex.exec(line);
        return {
          input: line,
          matches: match,
          groups: execResult ? execResult.slice(1) : []
        };
      });
      
      setTestResults(results);
      setRegexError('');
    } catch (error) {
      setRegexError(error.message);
      setTestResults([]);
    }
  }, [value, testStrings]);

  // Auto-test when regex or test strings change
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      testRegex();
    }, 300);
    
    return () => clearTimeout(timeoutId);
  }, [testRegex]);

  // Sample test strings for filename patterns
  const addSampleFilenames = () => {
    const samples = [
      'document.pdf',
      'report_2024.xlsx',
      'image-001.jpg',
      'data_export_20240814.csv',
      'temp_file.tmp',
      'backup.2024-08-14.zip',
      'user_profile_123.json',
      'log_file_error.txt'
    ].join('\n');
    
    setTestStrings(samples);
  };

  return (
    <div style={{ border: '1px solid #d9d9d9', borderRadius: 6, padding: 12, backgroundColor: '#fafafa' }}>
      <Space direction="vertical" style={{ width: '100%' }} size="small">
        {/* Regex Input */}
        <div>
          <Text strong>Regex Pattern:</Text>
          <Input
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder || "Enter regex pattern (e.g., .*\\.pdf$)"}
            status={regexError ? 'error' : ''}
          />
          {regexError && (
            <Alert
              message={`Invalid regex: ${regexError}`}
              type="error"
              size="small"
              style={{ marginTop: 4 }}
            />
          )}
        </div>

        <Divider style={{ margin: '8px 0' }} />

        {/* Test Input */}
        <div>
          <Space>
            <Text strong>Test Strings:</Text>
            <Button 
              size="small" 
              type="link" 
              onClick={addSampleFilenames}
              style={{ padding: 0 }}
            >
              Add Sample Filenames
            </Button>
          </Space>
          <TextArea
            value={testStrings}
            onChange={(e) => setTestStrings(e.target.value)}
            placeholder="Enter test strings (one per line)&#10;Example:&#10;document.pdf&#10;image.jpg&#10;data.csv"
            rows={4}
            style={{ fontSize: '12px' }}
          />
        </div>

        {/* Test Button */}
        <Button 
          icon={<PlayCircleOutlined />} 
          onClick={testRegex}
          disabled={!value || !testStrings.trim()}
          size="small"
        >
          Test Regex
        </Button>

        {/* Results */}
        {testResults.length > 0 && (
          <div>
            <Text strong>Test Results:</Text>
            <List
              size="small"
              dataSource={testResults}
              renderItem={(result) => (
                <List.Item style={{ padding: '4px 0' }}>
                  <Space>
                    {result.matches ? (
                      <CheckCircleOutlined style={{ color: '#52c41a' }} />
                    ) : (
                      <CloseCircleOutlined style={{ color: '#ff4d4f' }} />
                    )}
                    <Text 
                      code 
                      style={{ 
                        color: result.matches ? '#52c41a' : '#ff4d4f',
                        fontSize: '12px'
                      }}
                    >
                      {result.input}
                    </Text>
                    {result.groups.length > 0 && (
                      <Text type="secondary" style={{ fontSize: '11px' }}>
                        Groups: [{result.groups.join(', ')}]
                      </Text>
                    )}
                  </Space>
                </List.Item>
              )}
              style={{ 
                maxHeight: 150, 
                overflowY: 'auto',
                border: '1px solid #f0f0f0',
                borderRadius: 4,
                padding: '4px 8px',
                backgroundColor: '#fff'
              }}
            />
          </div>
        )}
      </Space>
    </div>
  );
};

export default RegexTester;
